import { Params } from '@angular/router';

// Tests if a URL is absolute or relative. It does not check if it's an absolute URL for the current site.
export function isAbsoluteUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const loweredUrl = url.toLowerCase();
  const absolutePrefixes = ['http://', 'https://'];
  return absolutePrefixes.some(p => loweredUrl.startsWith(p));
}

export function joinUrlPathSegments(segments: Array<string | undefined | null>) {
  // Filter for only truthy segments.
  const filteredSegments = segments.filter(segment => !!segment) as Array<string>;

  // Normalize segments by trimming slashes from the start and end, except for the first and last segments respectively.
  const normalizedSegments = filteredSegments.map<string>((segment, index) => {
    if (index === 0) {
      // Remove only the trailing slash for the first segment, if necessary.
      return segment.endsWith('/') ? segment.slice(0, -1) : segment;
    } else if (index === filteredSegments.length - 1) {
      // Remove only the leading slash for the last segment, if necessary.
      return segment.startsWith('/') ? segment.substring(1) : segment;
    }
    // Remove leading and trailing slashes for all other segments.
    return segment.replace(/(^\/|\/$)/g, '');
  });
  return normalizedSegments.join('/');
}

export function extractPathParams(routePath: string, urlPath: string): Params {
  const definitionSegments = routePath.split('/').filter(segment => !!segment);
  const paramSegments = urlPath.split('/').filter(segment => !!segment);

  const params: Params = {};

  // TODO: this makes the assumption that the definitionSegments and paramSegments are the same length and will match.
  for (let i = 0; i < definitionSegments.length; i++) {
    const definitionSegment = definitionSegments[i];
    const paramSegment = paramSegments[i];

    if (!definitionSegment.startsWith(':')) continue;

    const paramKey = definitionSegment.substring(1);
    params[paramKey] = paramSegment;
  }

  return params;
}

// Resolve a routePath with parameters, replacing the parameters with the values in the params object.
// Example '/contacts/:contactId/edit' with params of {contactId: 1234} returns '/contacts/1234/edit'
export function resolveUrlPath(routePath: string, params?: Params) {
  if (!params) return routePath;

  const hasLeadingSlash = routePath.startsWith('/');
  const hasTrailingSlash = routePath.endsWith('/');
  const pathSegments = routePath.split('/').filter(segment => !!segment);

  let endsInEmptyParam = false;
  const resolvedSegments = pathSegments.map<string>(segment => {
    endsInEmptyParam = false;
    if (!segment.startsWith(':')) return segment;
    const paramKey = segment.substring(1);
    const routeParam = params[paramKey];
    const resolvedRouteParm = (routeParam ?? '').toString();
    endsInEmptyParam = resolvedRouteParm === '';
    return resolvedRouteParm;
  });

  let resolvedPath = joinUrlPathSegments(resolvedSegments);
  if (hasLeadingSlash) resolvedPath = `/${resolvedPath}`;
  if (hasTrailingSlash || endsInEmptyParam) resolvedPath = `${resolvedPath}/`;

  return resolvedPath;
}

// The routePath is definition of a path like '/contacts/:contactId/edit'.
// The urlPath is a string like '/contacts/1234/edit'.
export function isMatchedPath(routePath: string, urlPath: string): boolean {
  // This helps to strip any query strings that may have been included with the path
  const urlParts = getUrlParts(urlPath);
  urlPath = urlParts?.path || urlPath;

  const pathSegments = routePath.split('/').filter(segment => !!segment);
  const splitUrlPath = urlPath.split('/').filter(segment => !!segment);
  const maxSegments = Math.max(splitUrlPath.length, pathSegments.length);

  const indices = range(0, maxSegments);
  const isMatch = indices.every(i => {
    const pathSegment = pathSegments[i];
    if (pathSegment === undefined) return false;

    const isVariable = pathSegment.startsWith(':');
    if (isVariable) return true;

    const urlSegment = splitUrlPath[i];
    if (urlSegment === undefined) return false;

    const segmentMatches = pathSegment.toLocaleLowerCase() === urlSegment.toLocaleLowerCase();
    if (!segmentMatches) return false;
    return true;
  });

  return isMatch;
}

// Inclusive start, exclusive end. Example 0, 5 returns [0, 1, 2, 3, 4]
function range(start: number, end: number) {
  return Array.from({ length: end - start }, (_, i) => start + i);
}

export function buildUrl(origin: string, path: string, query: string | URLSearchParams) {
  const queryIsURLSearchParams = query instanceof URLSearchParams;
  const resolvedQuery = queryIsURLSearchParams ? query.toString() : query;

  let url = joinUrlPathSegments([origin, path]);
  if (resolvedQuery) {
    url += `?${resolvedQuery}`;
  }
  return url;
}

// If the url is passed as non-absolute, it will be assumed to be a path.
// The relativeToBaseUrl will be removed from the url, so the path will be relative to the baseUrl.
export function getUrlParts(
  url: string,
  relativeToBaseUrl?: string,
): { path: string; queryParams: URLSearchParams } | undefined {
  // Replace any /#/ segment from the URL to prevent it from being seen as the fragment
  let resolvedUrl = url.replace('/#/', '/!/');

  relativeToBaseUrl = relativeToBaseUrl || getAbsoluteUrlWithBasePath();
  if (!URL.canParse(resolvedUrl)) {
    resolvedUrl = getAbsoluteUrl(resolvedUrl, relativeToBaseUrl);
  }

  if (!URL.canParse(resolvedUrl)) return;
  const parsedUrl = new URL(resolvedUrl);

  let parsedBaseUrl: URL | undefined;
  if (relativeToBaseUrl) {
    if (!URL.canParse(relativeToBaseUrl)) return;
    parsedBaseUrl = new URL(relativeToBaseUrl);
    if (parsedUrl.origin !== parsedBaseUrl.origin) return;
  }

  let resolvedPath = parsedUrl.pathname.replace('/!/', '/#/');
  if (parsedBaseUrl) {
    if (!resolvedPath.startsWith(parsedBaseUrl.pathname)) return;
    resolvedPath = resolvedPath.substring(parsedBaseUrl.pathname.length);
  }

  resolvedPath = resolvedPath.startsWith('/') ? resolvedPath.slice(1) : resolvedPath;

  return { path: resolvedPath, queryParams: parsedUrl.searchParams };
}

export function isMatchedUrl(url1: string, url2: string): boolean {
  url1 = url1.replace('/#/', '/');
  if (!URL.canParse(url1)) return false;
  const parsedUrl1 = new URL(url1);

  url2 = url2.replace('/#/', '/');
  if (!URL.canParse(url2)) return false;
  const parsedUrl2 = new URL(url2);

  const matchedOrigin = parsedUrl1.origin === parsedUrl2.origin;
  if (!matchedOrigin) return false;

  const matchedPath = parsedUrl1.pathname === parsedUrl2.pathname;
  if (!matchedPath) return false;

  const matchedSearchParams = isMatchedSearchParams(parsedUrl1.searchParams, parsedUrl2.searchParams);
  if (!matchedSearchParams) return false;

  return true;
}

// Function to compare if two URLSearchParams objects match
// considering case insensitivity and ignoring empty values.
export function isMatchedSearchParams(searchParams1: URLSearchParams, searchParams2: URLSearchParams): boolean {
  const normalizedSearchParams1 = normalizeSearchParams(searchParams1);
  const normalizedSearchParams2 = normalizeSearchParams(searchParams2);

  if (normalizedSearchParams1.size !== normalizedSearchParams2.size) return false;

  for (const [key1, value1] of normalizedSearchParams1) {
    const value2 = normalizedSearchParams2.get(key1);
    if (value2 === undefined) return false;

    const isMatched = isMatchedStringArray(value1, value2);
    if (!isMatched) return false;
  }

  return true;
}

// Normalizes URLSearchParams into a Map with keys as lowercase and values as arrays of lowercase strings.
function normalizeSearchParams(searchParams: URLSearchParams): Map<string, Array<string>> {
  const results = new Map<string, Array<string>>();

  for (const [key, value] of searchParams) {
    if (value == '') continue;
    const normalizedKey = key.toLowerCase();
    const normalizedValue = value.toLowerCase();
    if (!results.has(normalizedKey)) {
      results.set(normalizedKey, []);
    }
    const values = results.get(normalizedKey);
    if (!Array.isArray(values)) continue;
    values.push(normalizedValue);
  }
  return results;
}

// Compares two arrays of strings for equality, considering frequency of elements, for case-insensitive comparison.
function isMatchedStringArray(arr1: Array<string>, arr2: Array<string>): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const counter1: Record<string, number> = {};
  const counter2: Record<string, number> = {};

  // Count the frequency of each item in arr1
  for (const str of arr1) {
    counter1[str] = (counter1[str] || 0) + 1;
  }

  // Count the frequency of each string in arr2
  for (const str of arr2) {
    counter2[str] = (counter2[str] || 0) + 1;
  }

  // Compare the frequency counters
  for (const key in counter1) {
    if (!(key in counter2)) {
      return false;
    }
    if (counter1[key] !== counter2[key]) {
      return false;
    }
  }

  return true;
}

export function getBasePath() {
  const basePath = document.querySelector('base')?.getAttribute('href') || '';
  return basePath;
}

export function getAbsoluteUrlWithBasePath(stripTrailingSlash = false) {
  const origin = window.location.origin;
  const basePath = getBasePath();
  let absoluteUrlWithBase = origin + basePath;
  if (stripTrailingSlash && absoluteUrlWithBase.endsWith('/')) {
    absoluteUrlWithBase = absoluteUrlWithBase.slice(0, -1);
  }
  return absoluteUrlWithBase;
}

// Strips the origin and base path from the URL, returning the remaining relative path.
export function getRelativeUrl(url: string) {
  if (!url) return '';

  const origin = window.location.origin;
  const basePath = getBasePath();

  const absolutePrefix = (origin + basePath).toLowerCase();
  const urlLowered = url.toLowerCase();

  const isAbsoluteUrl = urlLowered.startsWith(absolutePrefix);
  if (!isAbsoluteUrl) return url;

  const relativeUrl = url.slice(absolutePrefix.length);
  return relativeUrl;
}

// If relativeto is not included, assumes the path should be relative to the
// current site (absoluteUrlWithBasePath).
export function getAbsoluteUrl(path: string, relativeTo?: string) {
  path = path || '';

  // If the path isn't a path but rather an absolute URL, return as is.
  const isAbsolute = isAbsoluteUrl(path);
  if (isAbsolute) return path;

  relativeTo = relativeTo || getAbsoluteUrlWithBasePath();

  // If the relativeTo is not an absolute URL, return the path as is.
  if (!URL.canParse(relativeTo)) return path;

  const baseUrl = URL.parse(relativeTo);
  if (!baseUrl) return path;

  const pathSegments = path.split('/').filter(segment => !!segment);
  const baseSegments = baseUrl.pathname.split('/').filter(segment => !!segment);
  const maxAllowedSegments = Math.min(pathSegments.length, baseSegments.length);

  // Find how many segments overlap at the beginning
  let i = 0;
  while (i < maxAllowedSegments && pathSegments[i].toLowerCase() === baseSegments[i].toLowerCase()) {
    i++;
  }

  // Remove the overlapping segments from the new path
  const uniquePathSegments = pathSegments.slice(i);

  const absoluteUrl = joinUrlPathSegments([baseUrl.origin, ...baseSegments, ...uniquePathSegments]);
  return absoluteUrl;
}
